import { useState } from "react";

const TRADING_DESK =
  "https://images.unsplash.com/photo-1748439281934-2803c6a3ee36?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjB0cmFkaW5nJTIwZGVzayUyMGRhcmt8ZW58MHx8fHwxNzc4ODkwMjM0fDA&ixlib=rb-4.1.0&q=85";

const WEALTH =
  "https://static.prod-images.emergentagent.com/jobs/c145874f-77be-43ac-ab7b-47d69079da54/images/55fcc8b13cb222f96b7f303dd1ef4ce26a1bffcd231f78fce632e2ff144f2de2.png";

const picks = [
  { ticker: "NVDA", thesis: "AI infrastructure compounder", since: "Jun '23", ret: "+148.2%" },
  { ticker: "META", thesis: "Ad recovery + efficiency cycle", since: "Jan '23", ret: "+96.4%" },
  { ticker: "COST", thesis: "Membership flywheel · long-term", since: "Mar '23", ret: "+41.7%" },
  { ticker: "GOOGL", thesis: "Search moat · AI tailwind", since: "Aug '23", ret: "+33.9%" },
  { ticker: "BRK.B", thesis: "Quality + downside protection", since: "Feb '23", ret: "+22.1%" },
];

// Compounding helper — monthly contribution, annualised rate
const futureValue = (monthly, years, annualRate) => {
  const r = annualRate / 12;
  const n = years * 12;
  return monthly * ((Math.pow(1 + r, n) - 1) / r);
};

const fmt = (n) =>
  "$" + Math.round(n).toLocaleString("en-US", { maximumFractionDigits: 0 });

export const Results = () => {
  const [monthly, setMonthly] = useState(250);

  const horizons = [10, 20, 30];
  const rate = 0.1; // realistic long-term equity return assumption

  return (
    <section
      id="results"
      data-testid="results-section"
      className="relative py-32 lg:py-40 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-20 max-w-3xl">
          <p className="text-xs uppercase tracking-[0.35em] text-amber-500 mb-6">
            — Track Record
          </p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight">
            Real picks. <em className="italic text-amber-400">Real returns.</em>
          </h2>
          <p className="mt-6 text-zinc-400 text-lg">
            Long-term conviction names from my personal portfolio. Held through
            the noise, paid by patience. The numbers aren't theoretical — they're
            still open.
          </p>
        </div>

        {/* Bento */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Big card */}
          <div
            className="md:col-span-7 relative rounded-3xl overflow-hidden border border-white/10 min-h-[420px] bg-[#0A0A0A]"
            data-testid="results-bento-chart"
          >
            <img
              src={TRADING_DESK}
              alt="Investing workspace"
              className="absolute inset-0 w-full h-full object-cover opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="relative p-8 lg:p-10 h-full flex flex-col justify-end">
              <span className="text-[10px] uppercase tracking-[0.3em] text-amber-400 mb-3">
                Long-term portfolio
              </span>
              <div className="flex items-end gap-6 flex-wrap">
                <div>
                  <div className="font-serif-display text-5xl lg:text-6xl text-white">
                    Patience <em className="italic text-amber-400">pays.</em>
                  </div>
                  <div className="text-sm text-zinc-400 mt-4 max-w-md">
                    Five positions, held long-term through the noise — and
                    still up.
                  </div>
                </div>
                <div className="relative w-44 h-20">
                  <svg viewBox="0 0 200 80" className="w-full h-full">
                    <polyline
                      points="0,72 20,64 40,54 60,46 80,38 100,30 120,22 140,16 160,12 180,8 200,4"
                      fill="none"
                      stroke="#fbbf24"
                      strokeWidth="2.5"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Stats stack */}
          <div className="md:col-span-5 grid grid-cols-2 gap-6">
            {[
              { v: "5 / 5", l: "Picks in the green", c: "text-emerald-400" },
              { v: "+68%", l: "Avg open return", c: "text-amber-400" },
              { v: "5+ yrs", l: "Avg holding period", c: "text-zinc-200" },
              { v: "0", l: "Forced exits", c: "text-zinc-200" },
            ].map((s) => (
              <div
                key={s.l}
                className="bg-[#0A0A0A] border border-white/10 rounded-3xl p-6 lg:p-8 hover:border-amber-600/40 transition-colors"
                data-testid={`stat-${s.l.replace(/\s/g,'-').toLowerCase()}`}
              >
                <div className={`font-serif-display text-4xl lg:text-5xl ${s.c}`}>
                  {s.v}
                </div>
                <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 mt-2">
                  {s.l}
                </div>
              </div>
            ))}
          </div>

          {/* Picks ledger */}
          <div
            className="md:col-span-7 rounded-3xl border border-white/10 bg-[#0A0A0A] p-8 lg:p-10"
            data-testid="results-ledger"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif-display text-2xl text-white">
                Conviction picks · live positions
              </h3>
              <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                Total return since entry
              </span>
            </div>
            <div className="divide-y divide-white/5">
              {picks.map((p) => (
                <div
                  key={p.ticker}
                  className="grid grid-cols-12 gap-4 py-4 items-center"
                >
                  <div className="col-span-3 font-serif-display text-lg text-white">
                    {p.ticker}
                  </div>
                  <div className="col-span-6 text-sm text-zinc-400">
                    {p.thesis}
                  </div>
                  <div className="col-span-3 text-right font-serif-display text-xl text-emerald-400">
                    {p.ret}
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-sans">
                      Held since {p.since}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Wealth concept */}
          <div
            className="md:col-span-5 relative rounded-3xl overflow-hidden border border-white/10 min-h-[320px]"
            data-testid="results-bento-wealth"
          >
            <img
              src={WEALTH}
              alt="Compounding mindset"
              className="absolute inset-0 w-full h-full object-cover opacity-80"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/40 to-transparent" />
            <div className="relative p-8 lg:p-10 h-full flex flex-col justify-end">
              <span className="text-[10px] uppercase tracking-[0.3em] text-amber-400 mb-3">
                Compounding mindset
              </span>
              <p className="font-serif-display text-2xl text-white leading-snug max-w-xs">
                "Money in a savings account is money quietly making the bank
                rich. Move it. Let it work."
              </p>
            </div>
          </div>
        </div>

        {/* ----- What if you start now? compounding section ----- */}
        <div
          id="compound"
          data-testid="compound-section"
          className="mt-20 rounded-3xl border border-amber-600/30 bg-gradient-to-b from-amber-950/30 to-[#0A0A0A] p-8 lg:p-14"
        >
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <p className="text-xs uppercase tracking-[0.35em] text-amber-500 mb-6">
                — What if you started now?
              </p>
              <h3 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
                A little, every month.
                <br />
                <em className="italic text-amber-400">A lot, eventually.</em>
              </h3>
              <p className="mt-6 text-zinc-400">
                Drag the slider. See what investing a steady amount each month
                could grow into over time, assuming a long-term annual return
                of ~10% (the historical equity average).
              </p>

              <div className="mt-10">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs uppercase tracking-[0.3em] text-zinc-500">
                    Monthly contribution
                  </span>
                  <span
                    className="font-serif-display text-3xl text-amber-400"
                    data-testid="compound-monthly-display"
                  >
                    {fmt(monthly)}
                  </span>
                </div>
                <input
                  type="range"
                  min={50}
                  max={2000}
                  step={50}
                  value={monthly}
                  onChange={(e) => setMonthly(Number(e.target.value))}
                  data-testid="compound-slider"
                  aria-label="Monthly contribution"
                  className="w-full h-2 bg-white/10 rounded-full appearance-none cursor-pointer accent-amber-500"
                />
                <div className="flex justify-between text-[10px] text-zinc-500 mt-2 uppercase tracking-widest">
                  <span>$50</span>
                  <span>$2,000</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-3 gap-4">
              {horizons.map((y) => {
                const fv = futureValue(monthly, y, rate);
                return (
                  <div
                    key={y}
                    data-testid={`compound-card-${y}`}
                    className="rounded-2xl border border-white/10 bg-black/40 p-6 lg:p-8"
                  >
                    <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">
                      In {y} years
                    </div>
                    <div className="font-serif-display text-4xl lg:text-5xl text-white mt-3">
                      {fmt(fv)}
                    </div>
                    <div className="mt-3 text-xs text-zinc-500">
                      You'd have contributed{" "}
                      <span className="text-zinc-300">
                        {fmt(monthly * 12 * y)}
                      </span>
                      .
                      <br />
                      The market did the rest.
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <p className="mt-10 text-[11px] text-zinc-500 max-w-2xl">
            Illustrative only. Assumes a constant 10% annualised return,
            monthly contributions reinvested, no fees or taxes. Actual returns
            vary and capital is always at risk.
          </p>
        </div>
      </div>
    </section>
  );
};
